// store/aiImportStore.ts
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { AiImportStatus, AiImportStatusResponse } from "@/graphql/__generated__/graphql";

type State = {
  aiImportStatuses:AiImportStatusResponse[];
};

type Actions = {
  setAIImportStatuses: (_aiImportStatuses: AiImportStatusResponse[]) => void;
  mergeAIImportStatuses: (_newStatuses: AiImportStatusResponse[]) => void;
  reset: () => void;
};

// Sorting function for AIImportStatuses
const sortAIImportStatuses = (statuses: AiImportStatusResponse[]): AiImportStatusResponse[] => {
  const order = [
    AiImportStatus.Pending,
    AiImportStatus.ExtractingDataFields,
    AiImportStatus.WritingExecutiveSummary,
    AiImportStatus.Failed,
    AiImportStatus.Success,
  ];
  return statuses.sort(
    (a, b) => order.indexOf(a.status) - order.indexOf(b.status)
  );
};

// Create the store with persistence
export const useAiImportStore = create<State & Actions>()(
  persist(
    (set) => ({
      aiImportStatuses: [],

      setAIImportStatuses: (aiImportStatuses) =>
        set({
          aiImportStatuses: sortAIImportStatuses(aiImportStatuses),
        }),

      mergeAIImportStatuses: (newStatuses) =>
        set((state) => {
          const mergedStatuses = [...state.aiImportStatuses];

          newStatuses.forEach((newStatus) => {
            const existingIndex = mergedStatuses.findIndex(
              (status) => status.id === newStatus.id
            );

            if (existingIndex >= 0) {
              // Overwrite the existing status with the new one
              mergedStatuses[existingIndex] = {
                ...mergedStatuses[existingIndex],
                ...newStatus,
              };
            } else {
              // Add the new status if not already present
              mergedStatuses.push(newStatus);
            }
          });

          return {
            aiImportStatuses: sortAIImportStatuses(mergedStatuses),
          };
        }),

      reset: () => set({ aiImportStatuses: [] }),
    }),
    {
      name: "ai-imports-store", // Name for the storage
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
